import { chainDynamicValue, DynamicValue } from "@faedev/dynamic-values";
import { composeClassNames, ifDefined } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import FormSelect from "src/components/FormSelect";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import gutter from "src/functions/gutter";
import isFormValid from "src/functions/isFormValid";
import onChangeToValue from "src/functions/onChangeToValue";
import optionsLocale from "src/functions/optionsLocale";
import validateAuthToken from "src/functions/validateAuthToken";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import { sNote } from "src/styles/sNote.module.scss";
import { DataClientForm } from "src/types/DataClientForm";

type Props = {
  userData: DynamicValue<DataClientForm>;
  setFormSave?: (setFormSave: boolean) => void;
};

function CartAddressForm({ userData, setFormSave }: Props) {
  const { locale: lang } = useRouter();
  var [resultState, setResultState] = useState(null);
  var [validated, setValidated] = useState(false);
  var [alertMessage, setAlertMessage] = useState(false);

  const userId = chainDynamicValue(userData, "userId").value;
  const name = chainDynamicValue(userData, "name").value;
  const surname = chainDynamicValue(userData, "surname").value;
  const email = chainDynamicValue(userData, "email").value;
  const phone = chainDynamicValue(userData, "phone").value;
  const street = chainDynamicValue(userData, "street").value;
  const city = chainDynamicValue(userData, "city").value;
  const zip = chainDynamicValue(userData, "zip").value;
  const country = chainDynamicValue(userData, "country").value;
  const isFilledName = chainDynamicValue(userData, "isFilledName").value;
  const isFilledSurname = chainDynamicValue(userData, "isFilledSurname").value;

  useEffect(() => {
    const timeId = setTimeout(() => {
      // After 10 seconds set the show value to false
      setAlertMessage(false);
      setValidated(false);
    }, 10000);

    return () => {
      clearTimeout(timeId);
    };
  }, [alertMessage]);

  return (
    <Form id={`ChangeAddress`} validated={validated} noValidate>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Name`} />*
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`name`}
              id={`name`}
              readOnly={isFilledName == "true" ?? true}
              value={chainDynamicValue(userData, "name").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "name").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Surname`} />*
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`surname`}
              id={`surname`}
              readOnly={isFilledSurname == "true" ?? true}
              value={chainDynamicValue(userData, "surname").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "surname").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Email`} />*
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`email`}
              id={`email`}
              readOnly
              value={chainDynamicValue(userData, "email").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "email").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
            <Form.Text className={sNote}>
              <Translate tKey={`The email format is name@domain.tld`} />
            </Form.Text>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Phone`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`phone`}
              id={`phone`}
              value={chainDynamicValue(userData, "phone").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "phone").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
            <Form.Text className={sNote}>
              <Translate
                tKey={`Enter the phone number, including the country code, eg +420 123 456 789`}
              />
            </Form.Text>
          </Form.Group>
        </Col>
      </Form.Row>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Street`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`street`}
              id={`street`}
              value={chainDynamicValue(userData, "street").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "street").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`City`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`city`}
              id={`city`}
              value={chainDynamicValue(userData, "city").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "city").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      <Form.Row className="justify-content-md-center">
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Zip`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              name={`zip`}
              id={`zip`}
              value={chainDynamicValue(userData, "zip").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "zip").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Country`} />
            </Form.Label>
            <FormSelect
              as={`select`}
              options={optionsLocale(`country`, lang)}
              className={sFormControl}
              id={`country`}
              name={`country`}
              value={chainDynamicValue(userData, "country").value}
              onChange={function (param2) {
                return chainDynamicValue(userData, "country").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      <Row className="text-center mb-4 mt-4">
        <Col>
          <Button
            variant={`primary`}
            type={`button`}
            className={composeClassNames([sButton, "btn-cta"])}
            onClick={function () {
              setValidated(true);
              if (isFormValid(`ChangeAddress`)) {
                setResultState(true);
                setAlertMessage(true);
                fetch(`${constants.Paths.apiUrl}/clients/${userId}`, {
                  method: "PUT",
                  headers: validateAuthToken(getCookieByName(`auth-token`)),
                  body: JSON.stringify({
                    name,
                    surname,
                    email,
                    phone,
                    address: { street, city, zip, country },
                  }),
                }).then(() => {
                  setFormSave(true);
                });
              }
            }}
          >
            <Translate tKey={`Save changes`} />
          </Button>
          {validated && !isFormValid(`ChangeAddress`) ? (
            <Alert
              variant={`danger`}
              className={sAlert}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <Translate
                tKey={`Uh-oh. Not all required informations are filled in!`}
              />
            </Alert>
          ) : null}
          {ifDefined(
            resultState,
            (value) =>
              value
                ? alertMessage && (
                    <Alert
                      variant={`success`}
                      className={sAlert}
                      style={gutter(`margin`, `vertical`, `md`)}
                    >
                      <Translate
                        tKey={`Great! The form was sent successfully.`}
                      />
                    </Alert>
                  )
                : alertMessage && (
                    <Alert
                      variant={`danger`}
                      className={sAlert}
                      style={gutter(`margin`, `vertical`, `md`)}
                    >
                      <Translate tKey={`Oops. The form could not be sent.`} />
                    </Alert>
                  ),
            () => null
          )}
        </Col>
      </Row>
    </Form>
  );
}

export default CartAddressForm;
